/**
 * A wall's length or a room's area typed over its label on the plan, as the app's numeric keypad opens over a tapped
 * dimension (`WallMarker` → `NumericKeypadPopup`, «Длина» / «Изменить длину»): a small card over the label with the
 * value selected, its unit, «Готово» and «Отмена». Enter applies, Escape and a press elsewhere on the plan put it away;
 * a value the plan refuses keeps the card open with the reason under it. While the plan's scale is being set the card
 * asks for the real length or area.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { DimensionLabel } from '../render/plan/PlanView';
import { chromeRects } from './chrome';
import { t } from '../i18n/I18n';

const format = (value: number, kind: DimensionLabel['kind']) => String(Math.round(value * (kind === 'wall' ? 1000 : 100)) / (kind === 'wall' ? 1000 : 100));

export function DimensionInput({ label, at, scaleMode, onSubmit, onClose }: {
  label: DimensionLabel;
  /** The label's middle, in the window's pixels. */
  at: { x: number; y: number };
  scaleMode: boolean;
  /** Applies the value; the reason it was refused, or null. */
  onSubmit: (value: number) => string | null;
  onClose: () => void;
}) {
  const [value, setValue] = useState(() => format(label.value, label.kind));
  const [error, setError] = useState('');
  const card = useRef<HTMLFormElement>(null), input = useRef<HTMLInputElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  // Over the label, kept inside the window; under it when there is no room above or the header, a banner or a panel
  // would be under the card there.
  useLayoutEffect(() => {
    const box = card.current?.getBoundingClientRect();
    if (!box) return;
    const margin = 12, left = Math.min(window.innerWidth - box.width - margin, Math.max(margin, at.x - box.width / 2));
    const panels = chromeRects();
    const covered = (top: number) => panels.reduce((sum, rect) =>
      sum + Math.max(0, Math.min(left + box.width, rect.x + rect.width) - Math.max(left, rect.x)) * Math.max(0, Math.min(top + box.height, rect.y + rect.height) - Math.max(top, rect.y)), 0);
    const above = at.y - box.height - 18, below = at.y + 18;
    const fitting = [above, below].filter(top => top >= margin && top + box.height <= window.innerHeight - margin);
    const top = fitting.sort((a, b) => covered(a) - covered(b))[0] ?? Math.min(window.innerHeight - box.height - margin, Math.max(margin, above));
    setPlace({ left, top });
  }, [at.x, at.y]);
  // Focused once placed: a card still being measured is not shown and would not take the focus.
  const focused = useRef(false);
  useEffect(() => {
    if (!place || focused.current) return;
    focused.current = true;
    input.current?.focus({ preventScroll: true }); input.current?.select();
  }, [place]);
  const title = scaleMode ? (label.kind === 'wall' ? t('#EditorRealWallLength') : t('#EditorRealRoomArea'))
    : label.kind === 'wall' ? t('#WallLength') : t('#EditorRoomArea');
  const submit = () => {
    const number = Number(value.trim().replace(',', '.'));
    if (!value.trim() || !Number.isFinite(number)) { setError(t('#EditorEnterNumber')); return; }
    const refused = onSubmit(number);
    if (refused) setError(refused); else onClose();
  };
  return <form ref={card} className="dimension-input" role="dialog" aria-label={title} data-placed={!!place}
    style={place ? { left: place.left, top: place.top } : { left: at.x, top: at.y, opacity: 0, pointerEvents: 'none' }}
    onSubmit={event => { event.preventDefault(); submit(); }}
    onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose(); } }}>
    <label><span>{title}</span>
      <span className="dimension-input-field"><input ref={input} inputMode="decimal" enterKeyHint="done" aria-label={title} aria-invalid={!!error}
        value={value} onChange={event => { setValue(event.target.value); setError(''); }} /><em>{label.kind === 'wall' ? t('m') : t('#UnitSquareMeters')}</em></span>
    </label>
    {scaleMode && <p className="dimension-input-note">{label.kind === 'wall' ? t('#EditorScaleByLengthNote') : t('#EditorScaleByAreaNote')}</p>}
    {error && <p className="dimension-input-error" role="alert">{error}</p>}
    <div className="dimension-input-actions">
      <button type="button" className="text-button" onClick={onClose}>{t('Cancel')}</button>
      <button type="submit" className="dimension-input-done">{t('Done')}</button>
    </div>
  </form>;
}
